import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Minus, Plus, ShoppingBag, Star } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { useBag } from "@/context/BagContext";
import { fetchProduct, fetchRelated } from "@/lib/api";
import { COLOR_META, COLLECTION_META, GRADIENTS, formatPrice } from "@/lib/meta";
import Placeholder from "@/components/Placeholder";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";

export default function ProductPage() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const { add } = useBag();
  const { data: product, isLoading } = useQuery({ queryKey: ["product", slug], queryFn: () => fetchProduct(slug) });
  const { data: related = [] } = useQuery({ queryKey: ["related", slug], queryFn: () => fetchRelated(slug) });

  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    setSize(null);
    setColor(null);
    setQty(1);
    setImgIdx(0);
  }, [slug]);

  useEffect(() => {
    if (product && product.sizes.length === 1) setSize(product.sizes[0]);
    if (product && product.colors.length) setColor(product.colors[0]);
  }, [product]);

  if (isLoading) {
    return (
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-24">
        <Placeholder variant={1} aspect="aspect-[3/4]" className="max-w-lg" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-xl mx-auto text-center py-32 px-6" data-testid="product-not-found">
        <p className="font-serif text-3xl text-ink mb-6">{t.product.notFound}</p>
        <Link to="/boutique" className="btn btn-ink" data-testid="product-back-to-shop">{t.product.backToShop}</Link>
      </div>
    );
  }

  const onAdd = () => {
    if (!size) {
      toast(t.product.selectSizeFirst);
      return;
    }
    add(product, size, color, qty);
    toast.success(`${t.common.added} — ${formatPrice(product.price * qty)}`);
  };

  const thumbs = [0, 1, 2, 3];

  return (
    <div className="pt-10 lg:pt-14 pb-24" data-testid="product-page">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
        <Link to="/boutique" className="text-[12px] uppercase tracking-[0.14em] text-ink/50 hover:text-rose-deep transition-colors" data-testid="product-breadcrumb">
          ← {t.product.backToShop}
        </Link>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 mt-8">
          {/* Gallery */}
          <div>
            <Placeholder
              variant={(product.gradient + imgIdx) % 6}
              aspect="aspect-[3/4]"
              className="rounded-sm"
            />
            <div className="flex gap-3 mt-4">
              {thumbs.map((i) => (
                <button
                  key={i}
                  onClick={() => setImgIdx(i)}
                  data-testid={`product-thumb-${i}`}
                  aria-label={`${t.common.placeholderLabel} ${i + 1}`}
                  className={`relative w-20 aspect-[3/4] overflow-hidden rounded-sm border-2 transition-all duration-200 ${
                    imgIdx === i ? "border-rose-deep" : "border-transparent hover:border-ink/30"
                  }`}
                >
                  <span className="absolute inset-0" style={{ backgroundImage: GRADIENTS[(product.gradient + i) % 6] }} />
                </button>
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="lg:pt-6">
            {product.tag && (
              <span className="inline-block bg-rose text-ivory text-[10px] uppercase tracking-[0.14em] px-3 py-1 rounded-full mb-5">
                {product.tag === "new" ? t.common.new : t.common.signature}
              </span>
            )}
            <h1 className="font-serif font-light text-4xl lg:text-5xl leading-tight text-ink" data-testid="product-name">
              {product.name[lang]}
            </h1>
            <p className="mt-2 text-[13px] text-ink/50">{COLLECTION_META[product.collection]?.[lang]}</p>

            <div className="flex items-baseline gap-3 mt-6" data-testid="product-price">
              <p className="font-serif text-3xl text-ink">{formatPrice(product.price)}</p>
              {product.compare_at && (
                <p className="text-ink/40 line-through text-lg">{formatPrice(product.compare_at)}</p>
              )}
            </div>

            <div className="mt-9">
              <p className="field-label">
                {t.common.color} — <span className="text-ink normal-case tracking-normal">{COLOR_META[color || "rose"]?.[lang]}</span>
              </p>
              <div className="flex gap-3 mt-1">
                {product.colors.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    data-testid={`product-color-${c}`}
                    title={COLOR_META[c]?.[lang]}
                    aria-label={COLOR_META[c]?.[lang]}
                    className={`w-9 h-9 rounded-full border-2 transition-all duration-200 ${
                      color === c ? "border-rose-deep scale-110" : "border-ink/15 hover:border-ink/40"
                    }`}
                    style={{ backgroundColor: COLOR_META[c]?.hex }}
                  />
                ))}
              </div>
            </div>

            <div className="mt-8">
              <p className="field-label">{t.product.chooseSize}</p>
              <div className="flex flex-wrap gap-2.5 mt-1">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    data-testid={`product-size-${s}`}
                    className={`py-2.5 px-5 rounded-full border text-[13px] transition-all duration-200 ${
                      size === s ? "bg-ink text-ivory border-ink" : "border-ink/25 text-ink/70 hover:border-rose-deep hover:text-rose-deep"
                    }`}
                  >
                    {s === "Unique" ? (lang === "fr" ? "Taille unique" : lang === "ar" ? "مقاس واحد" : "One size") : s}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-9 flex items-stretch gap-4">
              <div className="flex items-center border border-ink/25 rounded-full" data-testid="product-qty-stepper">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-3 text-ink/60 hover:text-rose-deep" data-testid="product-qty-minus" aria-label="-">
                  <Minus className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <span className="w-8 text-center text-sm" data-testid="product-qty-value">{qty}</span>
                <button onClick={() => setQty((q) => Math.min(20, q + 1))} className="px-4 py-3 text-ink/60 hover:text-rose-deep" data-testid="product-qty-plus" aria-label="+">
                  <Plus className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
              <button onClick={onAdd} className="btn btn-ink flex-1" data-testid="product-add-to-bag">
                <ShoppingBag className="w-4 h-4" strokeWidth={1.5} />
                {t.common.addToBag}
              </button>
            </div>

            <Accordion type="multiple" defaultValue={["desc"]} className="mt-12">
              <AccordionItem value="desc" className="border-ink/10">
                <AccordionTrigger className="text-[13px] uppercase tracking-[0.14em] hover:text-rose-deep hover:no-underline" data-testid="product-accordion-description">
                  {t.product.description}
                </AccordionTrigger>
                <AccordionContent className="text-ink/70 text-[15px] leading-relaxed">
                  {product.desc[lang]}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="details" className="border-ink/10">
                <AccordionTrigger className="text-[13px] uppercase tracking-[0.14em] hover:text-rose-deep hover:no-underline" data-testid="product-accordion-details">
                  {t.product.details}
                </AccordionTrigger>
                <AccordionContent className="text-ink/70 text-[15px] leading-relaxed">
                  {product.fabric[lang]}
                  <br />
                  {t.product.careBody}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="ship" className="border-ink/10">
                <AccordionTrigger className="text-[13px] uppercase tracking-[0.14em] hover:text-rose-deep hover:no-underline" data-testid="product-accordion-shipping">
                  {t.product.shippingInfo}
                </AccordionTrigger>
                <AccordionContent className="text-ink/70 text-[15px] leading-relaxed">
                  {t.product.shippingBody}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-24 lg:mt-32" data-testid="product-related">
            <Reveal>
              <h2 className="font-serif font-light text-3xl lg:text-4xl text-ink mb-10">{t.product.alsoLike}</h2>
            </Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 lg:gap-x-8 gap-y-12">
              {related.slice(0, 4).map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
